/**
 * Shell chat messenger bus — same idea as mailService.compose.
 *
 * Hosted modules (ERP task AccountChip) cannot import connect source; they call
 * `window.formica.openChat(...)` which is installed by `installChatFormicaBridge`.
 * ShellLayout registers a handler that opens the floating ChatPanel via
 * useShellChat. Without a handler, callers fall back to the full /chat page.
 */

export type ChatMessengerIntent = {
  /** Open (or create) a DM with this Connect account id. */
  accountId?: number | null;
  name?: string | null;
  email?: string | null;
  /** Optional existing channel id (skip DM lookup). */
  channelId?: number | null;
};

type OpenHandler = (intent: ChatMessengerIntent) => void;
type NavigationFallback = (intent: ChatMessengerIntent) => void;

let activeHandler: OpenHandler | null = null;
let pendingIntent: ChatMessengerIntent | null = null;
let navigationFallback: NavigationFallback | null = null;

export const chatService = {
  open(intent: ChatMessengerIntent = {}): void {
    if (activeHandler) {
      activeHandler(intent);
      return;
    }
    pendingIntent = intent;
    navigationFallback?.(intent);
  },
};

export function _setChatNavigationFallback(fn: NavigationFallback | null): void {
  navigationFallback = fn;
}

export function registerChatMessengerHandler(handler: OpenHandler): () => void {
  activeHandler = handler;
  drainPending(handler);
  return () => {
    if (activeHandler === handler) activeHandler = null;
  };
}

export function resetChatServiceForTests(): void {
  activeHandler = null;
  pendingIntent = null;
  navigationFallback = null;
}

function drainPending(handler: OpenHandler): void {
  if (!pendingIntent) return;
  const queued = pendingIntent;
  pendingIntent = null;
  queueMicrotask(() => handler(queued));
}

/** Bridge for hosted modules that cannot import connect source. */
export function installChatFormicaBridge(): void {
  if (typeof window === 'undefined') return;
  const bridged = window as Window & {
    formica?: {
      openChat?: (intent?: ChatMessengerIntent) => void;
    };
  };
  bridged.formica = {
    ...bridged.formica,
    openChat: (intent) => chatService.open(intent ?? {}),
  };
}

export function defaultChatNavigationFallback(intent: ChatMessengerIntent): void {
  const q = new URLSearchParams();
  if (intent.accountId) q.set('with', String(intent.accountId));
  const qs = q.toString();
  const path = qs ? `/chat?${qs}` : '/chat';
  if (window.location.hash.startsWith('#/')) {
    window.location.hash = `#${path}`;
    return;
  }
  window.location.assign(path);
}
